/**
 * useQuickReference Hook - MA-ARCH-161
 * 
 * Extracted from ScriptPlay.jsx to isolate quick reference tools:
 * - Calendar (date selection)
 * - Notepad (text notes)
 * 
 * State is persisted to localStorage per run.
 * 
 * Usage:
 * const quickRef = useQuickReference(runId);
 * quickRef.setCalendarSelected(new Date());
 * quickRef.setNotesText('Follow up on...');
 */

import { useState, useEffect, useMemo } from 'react';

export function useQuickReference(runId) {
  // Storage keys based on run ID
  const calendarStorageKey = useMemo(() => {
    if (!runId) return null;
    return `missionassist_run_${runId}_calendar_selected`;
  }, [runId]);

  const notesStorageKey = useMemo(() => {
    if (!runId) return null;
    return `missionassist_run_${runId}_notes`;
  }, [runId]);

  // Calendar state
  const [calendarSelected, setCalendarSelected] = useState(null);

  // Notes state
  const [notesText, setNotesText] = useState("");

  // Load calendar from localStorage
  useEffect(() => {
    if (!calendarStorageKey) return;
    try {
      const raw = localStorage.getItem(calendarStorageKey);
      setCalendarSelected(raw ? new Date(raw) : null);
    } catch {
      // ignore
    }
  }, [calendarStorageKey]);

  // Persist calendar to localStorage
  useEffect(() => {
    if (!calendarStorageKey) return;
    try {
      if (!calendarSelected) {
        localStorage.removeItem(calendarStorageKey);
      } else {
        localStorage.setItem(calendarStorageKey, calendarSelected.toISOString());
      }
    } catch {
      // ignore
    }
  }, [calendarSelected, calendarStorageKey]);

  // Load notes from localStorage
  useEffect(() => {
    if (!notesStorageKey) return;
    try {
      const raw = localStorage.getItem(notesStorageKey);
      setNotesText(raw || "");
    } catch {
      // ignore
    }
  }, [notesStorageKey]);

  // Persist notes to localStorage
  useEffect(() => {
    if (!notesStorageKey) return;
    try {
      localStorage.setItem(notesStorageKey, notesText || "");
    } catch {
      // ignore
    }
  }, [notesStorageKey, notesText]);

  return {
    // Calendar
    calendarSelected,
    setCalendarSelected,
    // Notes
    notesText,
    setNotesText,
  };
}

export default useQuickReference;
